// GainLossChart — Recharts ComposedChart: side-by-side bars (joined/separated)
// + prominent net labels + cumulative YTD line overlay

import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  LabelList,
} from "recharts";
import { useTheme } from "../../hooks/useTheme";

export interface MonthlyGainLoss {
  /** Display label — e.g. "Jan '25" */
  month: string;
  joined: number;
  separated: number;
  net: number;
}

/** Augmented row with cumulative net for the YTD line */
interface ChartRow extends MonthlyGainLoss {
  cumulative: number;
}

interface GainLossChartProps {
  data: MonthlyGainLoss[];
  isLoading?: boolean;
  /** Show net change annotations above each bar group */
  showNetLabels?: boolean;
}

interface TooltipPayload {
  name: string;
  value: number;
  color: string;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const joined = payload.find((p) => p.name === "Joined");
  const separated = payload.find((p) => p.name === "Separated");
  const cumulative = payload.find((p) => p.name === "Cumulative Net");

  const netVal = (joined?.value ?? 0) - (separated?.value ?? 0);

  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[150px]">
      <p className="font-semibold text-theme-primary mb-1.5">{label}</p>
      {joined && (
        <div className="flex justify-between gap-4">
          <span className="text-theme-muted">Joined</span>
          <span className="font-bold text-status-green">+{joined.value}</span>
        </div>
      )}
      {separated && (
        <div className="flex justify-between gap-4">
          <span className="text-theme-muted">Separated</span>
          <span className="font-bold text-status-red">-{separated.value}</span>
        </div>
      )}
      <div className="flex justify-between gap-4 mt-1 pt-1 border-t border-theme-subtle">
        <span className="text-theme-muted font-semibold">Net</span>
        <span
          className={`font-bold text-sm ${netVal > 0 ? "text-status-green" : netVal < 0 ? "text-status-red" : "text-theme-muted"}`}
        >
          {netVal >= 0 ? "+" : ""}
          {netVal}
        </span>
      </div>
      {cumulative !== undefined && (
        <div className="flex justify-between gap-4 mt-0.5">
          <span className="text-theme-muted">YTD Cumulative</span>
          <span
            className={`font-semibold ${(cumulative.value ?? 0) >= 0 ? "text-status-green" : "text-status-red"}`}
          >
            {(cumulative.value ?? 0) >= 0 ? "+" : ""}
            {cumulative.value}
          </span>
        </div>
      )}
    </div>
  );
}

/** Custom label renderer for the net value above joined bars — bold and large */
function NetLabel(
  props: Record<
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  > & {
    index?: number;
    data?: ChartRow[];
  },
) {
  const { x = 0, y = 0, width = 0, index = 0, data = [] } = props;
  const xNum = Number(x);
  const yNum = Number(y);
  const widthNum = Number(width);
  const row = data[index];
  if (!row) return null;

  const net = row.net;
  if (net === 0 && row.joined === 0 && row.separated === 0) return null;

  const label = net >= 0 ? `+${net}` : String(net);
  const color = net > 0 ? "#16a34a" : net < 0 ? "#dc2626" : "#9ca3af";

  const barGroupCenterX = xNum + widthNum / 2;
  const labelY = yNum - 8;

  return (
    <text
      x={barGroupCenterX}
      y={labelY}
      textAnchor="middle"
      fill={color}
      fontSize={12}
      fontWeight="800"
      fontFamily="system-ui, sans-serif"
    >
      {label}
    </text>
  );
}

export default function GainLossChart({
  data,
  isLoading = false,
  showNetLabels = false,
}: GainLossChartProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  // Build cumulative net column for YTD line
  const chartData: ChartRow[] = data.reduce<ChartRow[]>((acc, row) => {
    const prev = acc[acc.length - 1];
    const cumulative = (prev?.cumulative ?? 0) + row.net;
    acc.push({ ...row, cumulative });
    return acc;
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {/* Legend skeleton */}
        <div className="flex items-center gap-4 px-4">
          <div className="skeleton h-3 w-20 rounded" />
          <div className="skeleton h-3 w-20 rounded" />
          <div className="skeleton h-3 w-28 rounded" />
        </div>
        <div className="flex items-end gap-2 h-52 px-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="flex-1 flex flex-col gap-1 items-center">
              <div
                className="skeleton w-full rounded-t-sm"
                style={{ height: `${30 + ((i * 17 + 23) % 80)}%` }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-52 gap-2">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-theme-muted opacity-40"
        >
          <path d="M3 3v18h18" />
          <path d="m19 9-5 5-4-4-3 3" />
        </svg>
        <p className="text-sm text-theme-muted">No member data available</p>
        <p className="text-xs text-theme-muted opacity-60">
          Data syncing — check back after Airtable sync completes
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Legend row — hand-rolled for clarity */}
      <div className="flex items-center gap-5 px-1 pb-1 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#16a34a] opacity-85 shrink-0" />
          <span className="text-xs text-theme-secondary">Joined</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-sm bg-[#dc2626] opacity-85 shrink-0" />
          <span className="text-xs text-theme-secondary">Separated</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="w-6 h-0.5 rounded-full"
            style={{ background: isDark ? "#60a5fa" : "#2563eb" }}
          />
          <span className="text-xs text-theme-secondary">
            Cumulative Net (YTD)
          </span>
        </div>
        {showNetLabels && (
          <div className="flex items-center gap-1.5 ml-auto">
            <span className="text-xs font-bold text-status-green">+N</span>
            <span className="text-xs text-theme-muted">/</span>
            <span className="text-xs font-bold text-status-red">-N</span>
            <span className="text-xs text-theme-muted">= monthly net</span>
          </div>
        )}
      </div>

      <ResponsiveContainer width="100%" height={270}>
        <ComposedChart
          data={chartData}
          margin={{ top: 28, right: 20, left: 0, bottom: 0 }}
          barCategoryGap="20%"
        >
          <CartesianGrid
            vertical={false}
            stroke={gridColor}
            strokeDasharray="3 3"
          />
          <XAxis
            dataKey="month"
            tick={{ fontSize: 11, fill: axisColor }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: axisColor }}
            tickLine={false}
            axisLine={false}
            width={32}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{
              fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
            }}
          />
          {/* No Recharts Legend — we use our hand-rolled one above */}
          <ReferenceLine y={0} stroke={gridColor} strokeWidth={1} />

          {/* Joined bars — green */}
          <Bar
            dataKey="joined"
            name="Joined"
            fill="#16a34a"
            fillOpacity={0.85}
            radius={[2, 2, 0, 0]}
            maxBarSize={24}
          >
            {showNetLabels && (
              <LabelList
                content={(props) => <NetLabel {...props} data={chartData} />}
              />
            )}
          </Bar>

          {/* Separated bars — red */}
          <Bar
            dataKey="separated"
            name="Separated"
            fill="#dc2626"
            fillOpacity={0.85}
            radius={[2, 2, 0, 0]}
            maxBarSize={24}
          />

          {/* Cumulative YTD net line */}
          <Line
            type="monotone"
            dataKey="cumulative"
            name="Cumulative Net"
            stroke={isDark ? "#60a5fa" : "#2563eb"}
            strokeWidth={2}
            dot={{ r: 3, fill: isDark ? "#60a5fa" : "#2563eb", strokeWidth: 0 }}
            activeDot={{ r: 5, strokeWidth: 0 }}
            strokeDasharray="4 2"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
